/**
 * Read-aloud using the browser's built-in text-to-speech (Web Speech API).
 * Works offline on most devices; voices vary, so we pick the friendliest one available.
 */
const PREFERRED = [
  'Samantha',
  'Google US English',
  'Microsoft Aria',
  'Microsoft Jenny',
  'Microsoft Ana',
  'Karen',
  'Tessa',
  'Moira',
  'Google UK English Female',
  'Daniel',
];

export class Voice {
  enabled = true;
  private voice: SpeechSynthesisVoice | null = null;
  private synth: SpeechSynthesis | null =
    typeof window !== 'undefined' ? (window.speechSynthesis ?? null) : null;

  constructor() {
    if (!this.synth) return;
    const pick = () => (this.voice = this.choose());
    pick();
    this.synth.addEventListener?.('voiceschanged', pick);
  }

  get supported() {
    return !!this.synth;
  }

  private choose(): SpeechSynthesisVoice | null {
    const voices = this.synth?.getVoices() ?? [];
    const en = voices.filter((v) => v.lang?.toLowerCase().startsWith('en'));
    for (const name of PREFERRED) {
      const v = en.find((x) => x.name.includes(name));
      if (v) return v;
    }
    return en.find((v) => v.localService) ?? en[0] ?? voices[0] ?? null;
  }

  /** Must be called from a tap once, so later speech is allowed (iOS). */
  unlock() {
    if (!this.synth) return;
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    this.synth.speak(u);
  }

  say(text: string, onEnd?: () => void): void {
    if (!this.synth || !this.enabled) {
      onEnd?.();
      return;
    }
    const clean = text
      .replace(/[\p{Extended_Pictographic}\u{FE0F}]/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!clean) return;
    this.synth.cancel();
    const u = new SpeechSynthesisUtterance(clean);
    if (this.voice) u.voice = this.voice;
    u.lang = this.voice?.lang ?? 'en-US';
    u.rate = 1.0;
    u.pitch = 1.2;
    u.onend = () => onEnd?.();
    u.onerror = () => onEnd?.();
    this.synth.speak(u);
  }

  stop() {
    this.synth?.cancel();
  }
}
